/**
 * @file CustomerLegalEntitiesSection.tsx
 * @description Секция «Организации» в карточке клиента (phase10.6.B2B).
 *
 * Показывает все привязанные через CustomerLegalEntity юр.лица + их р/счета.
 * Менеджер может:
 *   - Привязать существующее в магазине ЮЛ (через LegalEntityAutocomplete)
 *   - Отметить ЮЛ как «по умолчанию»
 *   - Отвязать ЮЛ от клиента (сама запись LegalEntity не удаляется)
 *
 * Клиент сам управляет реквизитами + расчётными счетами через ЛК на apps/web.
 * Здесь р/счета — read-only.
 *
 * phase19.31: подписи не мельче 12px и без капса (SC43), флажок «Сделать
 * основным» — общий `Checkbox` в строке 48px.
 */

import { useEffect, useState } from "react";

import { LegalEntityAutocomplete } from "../../../../components/customers/LegalEntityAutocomplete";
import { Alert } from "../../../../components/ui/alert";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { Checkbox } from "../../../../components/ui/checkbox";
import { EditorialCard } from "../../../../components/ui/EditorialCard";
import { useNotify } from "../../../../components/ui/NotifyProvider";
import { ApiError } from "../../../../lib/api-client";
import {
  TAX_SYSTEM_LABEL,
  customerLegalEntities,
  type CustomerLegalEntityLinkDto,
} from "../../../../lib/api-customers";

interface Props {
  customerId: string;
  canManage: boolean;
}

export function CustomerLegalEntitiesSection({ customerId, canManage }: Props) {
  const notify = useNotify();
  const [links, setLinks] = useState<CustomerLegalEntityLinkDto[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attaching, setAttaching] = useState(false);
  const [pickerId, setPickerId] = useState<string | null>(null);
  const [pickerPrimary, setPickerPrimary] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      setLinks(await customerLegalEntities.list(customerId));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  async function handleAttach() {
    if (!pickerId) return;
    setAttaching(true);
    try {
      await customerLegalEntities.attach(customerId, pickerId, pickerPrimary);
      notify.success("Юр.лицо привязано");
      setPickerId(null);
      setPickerPrimary(false);
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось");
    } finally {
      setAttaching(false);
    }
  }

  async function handleSetPrimary(legalEntityId: string) {
    try {
      await customerLegalEntities.setPrimary(customerId, legalEntityId);
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось");
    }
  }

  async function handleDetach(legalEntityId: string, name: string) {
    if (!confirm(`Отвязать «${name}» от клиента?`)) return;
    try {
      await customerLegalEntities.detach(customerId, legalEntityId);
      notify.success("Отвязано");
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось");
    }
  }

  return (
    <EditorialCard>
      <EditorialCard.Body>
        <div className="label-editorial mb-3">Организации (B2B)</div>

        {loading ? (
          <div className="text-sm text-on-surface-variant">Загружаем…</div>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !links || links.length === 0 ? (
          <p className="text-sm text-on-surface-variant">
            Клиент не привязан ни к одному юр.лицу. Все заказы оформляются как физлицо.
          </p>
        ) : (
          <ul className="flex flex-col gap-3">
            {links.map((l) => (
              <li
                key={l.linkId}
                className="rounded-lg border border-on-surface/8 bg-surface p-3"
              >
                <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                  <div className="flex min-w-0 flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">
                        {l.legalEntity.displayName}
                      </span>
                      {l.isPrimary ? (
                        <Badge variant="primary">По умолчанию</Badge>
                      ) : null}
                      {!l.legalEntity.isActive ? (
                        <Badge variant="default">неактивно</Badge>
                      ) : null}
                    </div>
                    <div className="text-xs text-on-surface-variant">
                      ИНН {l.legalEntity.inn}
                      {l.legalEntity.kpp ? ` · КПП ${l.legalEntity.kpp}` : ""}
                      {l.legalEntity.ogrn ? ` · ОГРН ${l.legalEntity.ogrn}` : ""}
                    </div>
                    <div className="text-xs text-on-surface-variant">
                      {l.legalEntity.legalAddress}
                    </div>
                    {l.legalEntity.taxSystem ? (
                      <div className="text-xs text-on-surface-variant">
                        {TAX_SYSTEM_LABEL[l.legalEntity.taxSystem]}
                        {l.legalEntity.vatRequired ? " · с НДС" : " · без НДС"}
                      </div>
                    ) : null}
                  </div>
                  {canManage ? (
                    <div className="flex shrink-0 flex-wrap gap-2">
                      {!l.isPrimary ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => void handleSetPrimary(l.legalEntity.id)}
                        >
                          По умолчанию
                        </Button>
                      ) : null}
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() =>
                          void handleDetach(
                            l.legalEntity.id,
                            l.legalEntity.displayName,
                          )
                        }
                      >
                        Отвязать
                      </Button>
                    </div>
                  ) : null}
                </div>

                {l.legalEntity.bankAccounts.length > 0 ? (
                  <div className="mt-3 border-t border-on-surface/8 pt-3">
                    <div className="type-caption mb-2 font-medium text-on-surface-variant">
                      Расчётные счета ({l.legalEntity.bankAccounts.length})
                    </div>
                    <ul className="flex flex-col gap-1.5">
                      {l.legalEntity.bankAccounts.map((b) => (
                        <li
                          key={b.id}
                          className="flex flex-wrap items-center gap-2 text-xs"
                        >
                          <span className="font-medium">{b.bankName}</span>
                          {b.isDefault ? (
                            <Badge variant="primary">По умолч.</Badge>
                          ) : null}
                          <span className="font-mono text-on-surface-variant">
                            р/с {b.accountNumber}
                          </span>
                          <span className="text-on-surface-variant">
                            · БИК {b.bik}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {canManage ? (
          <div className="mt-4 flex flex-col gap-3 border-t border-on-surface/8 pt-4">
            <div className="type-body font-medium text-on-surface">Привязать ещё юрлицо</div>
            <LegalEntityAutocomplete value={pickerId} onChange={setPickerId} />
            {pickerId ? (
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex min-h-12 cursor-pointer items-center gap-2 type-body">
                  <Checkbox checked={pickerPrimary} onCheckedChange={(c) => setPickerPrimary(c === true)} />
                  <span>Сделать основным</span>
                </label>
                <Button
                  onClick={() => void handleAttach()}
                  disabled={attaching}
                >
                  {attaching ? "Привязываем…" : "Привязать"}
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </EditorialCard.Body>
    </EditorialCard>
  );
}
